import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, where, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';

interface Product {
  id: string;
  name: string;
  type: 'motion_pack' | 'voice_pack' | 'training_pack' | 'theme' | 'achievement_pack';
  category: string;
  description: string;
  price: number;
  currency: 'USD' | 'EUR' | 'HUF';
  discountPrice?: number;
  featured: boolean;
  enabled: boolean;
  fileUrl: string;
  thumbnailUrl: string;
  fileSize: number;
  version: string;
  requirements: string[];
  tags: string[];
  downloads: number;
  rating: number;
  reviewCount: number;
  createdBy: string;
  createdAt: Date;
}

interface ProductCatalogProps {
  showAddModal: boolean;
  setShowAddModal: (show: boolean) => void;
}

const ProductCatalog: React.FC<ProductCatalogProps> = ({ showAddModal, setShowAddModal }) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Product | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [form, setForm] = useState({
    name: '',
    type: 'motion_pack' as Product['type'],
    category: '',
    description: '',
    price: 0,
    currency: 'USD' as Product['currency'],
    discountPrice: 0,
    featured: false,
    enabled: true,
    fileUrl: '',
    thumbnailUrl: '',
    fileSize: 0,
    version: '1.0.0',
    requirements: [] as string[],
    tags: [] as string[],
    createdBy: 'admin'
  });

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'marketplace_products'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      })) as Product[];
      
      setProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const productData = {
        name: form.name,
        type: form.type,
        category: form.category,
        description: form.description,
        price: form.price,
        currency: form.currency,
        discountPrice: form.discountPrice || null,
        featured: form.featured,
        enabled: form.enabled,
        fileUrl: form.fileUrl,
        thumbnailUrl: form.thumbnailUrl,
        fileSize: form.fileSize,
        version: form.version,
        requirements: form.requirements,
        tags: form.tags,
        createdBy: form.createdBy,
        downloads: editing ? editing.downloads : 0,
        rating: editing ? editing.rating : 0,
        reviewCount: editing ? editing.reviewCount : 0,
        updatedAt: Timestamp.now()
      };

      if (editing) {
        await updateDoc(doc(db, 'marketplace_products', editing.id), productData);
      } else {
        await addDoc(collection(db, 'marketplace_products'), {
          ...productData,
          createdAt: Timestamp.now()
        });
      }

      setShowAddModal(false);
      setEditing(null);
      resetForm();
      loadProducts();
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('marketplace.confirmDeleteProduct'))) {
      try {
        await deleteDoc(doc(db, 'marketplace_products', id));
        loadProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  const handleEdit = (product: Product) => {
    setEditing(product);
    setForm({
      name: product.name,
      type: product.type,
      category: product.category,
      description: product.description,
      price: product.price,
      currency: product.currency,
      discountPrice: product.discountPrice || 0,
      featured: product.featured,
      enabled: product.enabled,
      fileUrl: product.fileUrl,
      thumbnailUrl: product.thumbnailUrl,
      fileSize: product.fileSize,
      version: product.version,
      requirements: product.requirements,
      tags: product.tags,
      createdBy: product.createdBy
    });
    setShowAddModal(true);
  };

  const resetForm = () => {
    setForm({
      name: '',
      type: 'motion_pack',
      category: '',
      description: '',
      price: 0,
      currency: 'USD',
      discountPrice: 0,
      featured: false,
      enabled: true,
      fileUrl: '',
      thumbnailUrl: '',
      fileSize: 0,
      version: '1.0.0',
      requirements: [],
      tags: [],
      createdBy: 'admin'
    });
  };

  const addRequirement = () => {
    setForm({ ...form, requirements: [...form.requirements, ''] });
  };

  const updateRequirement = (index: number, value: string) => {
    const newRequirements = [...form.requirements];
    newRequirements[index] = value;
    setForm({ ...form, requirements: newRequirements });
  };

  const removeRequirement = (index: number) => {
    setForm({ ...form, requirements: form.requirements.filter((_, i) => i !== index) });
  };

  const addTag = () => {
    setForm({ ...form, tags: [...form.tags, ''] });
  };

  const updateTag = (index: number, value: string) => {
    const newTags = [...form.tags];
    newTags[index] = value;
    setForm({ ...form, tags: newTags });
  };

  const removeTag = (index: number) => {
    setForm({ ...form, tags: form.tags.filter((_, i) => i !== index) });
  };

  const getTypeIcon = (type: Product['type']) => {
    switch (type) {
      case 'motion_pack': return '🎬';
      case 'voice_pack': return '🎤';
      case 'training_pack': return '💪';
      case 'theme': return '🎨';
      case 'achievement_pack': return '🏆';
      default: return '📦';
    }
  };

  const formatPrice = (price: number, currency: string) => {
    const symbols: { [key: string]: string } = { USD: '$', EUR: '€', HUF: 'Ft' };
    return `${symbols[currency] || currency} ${price.toLocaleString()}`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const filteredProducts = filterType === 'all' 
    ? products 
    : products.filter(p => p.type === filterType);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-[#B8B8C3]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 overflow-x-hidden max-w-full">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilterType('all')}
            className={`px-3 py-2 rounded-lg transition-colors whitespace-nowrap text-sm ${
              filterType === 'all' ? 'bg-cyan-500 text-white' : 'bg-[#15191E] text-[#B8B8C3] hover:bg-[#1d232a]'
            }`}
          >
            {t('common.all')}
          </button>
          {['motion_pack', 'voice_pack', 'training_pack', 'theme', 'achievement_pack'].map(type => (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              className={`px-3 py-2 rounded-lg transition-colors whitespace-nowrap text-sm ${
                filterType === type ? 'bg-cyan-500 text-white' : 'bg-[#15191E] text-[#B8B8C3] hover:bg-[#1d232a]'
              }`}
            >
              {getTypeIcon(type as Product['type'])} {t(`marketplace.productType.${type}`)}
            </button>
          ))}
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditing(null);
            setShowAddModal(true);
          }}
          className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto whitespace-nowrap"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('marketplace.newProduct')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-full">
        {filteredProducts.map(product => (
          <div key={product.id} className="card-dark p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{getTypeIcon(product.type)}</span>
                <div>
                  <h4 className="text-lg font-bold text-white">{product.name}</h4>
                  <p className="text-xs text-[#B8B8C3]">v{product.version}</p>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                {product.featured && (
                  <span className="px-2 py-1 rounded-full text-xs font-semibold bg-yellow-500/20 text-yellow-400">
                    ⭐ {t('marketplace.featured')}
                  </span>
                )}
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  product.enabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                }`}>
                  {product.enabled ? t('marketplace.available') : t('marketplace.unavailable')}
                </span>
              </div>
            </div>

            {product.thumbnailUrl && (
              <div className="mb-4 rounded-lg overflow-hidden bg-[#15191E] h-32 flex items-center justify-center">
                <img src={product.thumbnailUrl} alt={product.name} className="max-h-full max-w-full object-contain" />
              </div>
            )}

            <p className="text-sm text-[#B8B8C3] mb-4 line-clamp-2">{product.description}</p>

            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-cyan-400">
                  {product.discountPrice ? (
                    <>
                      <span className="line-through text-gray-500 text-lg mr-2">
                        {formatPrice(product.price, product.currency)}
                      </span>
                      {formatPrice(product.discountPrice, product.currency)}
                    </>
                  ) : (
                    formatPrice(product.price, product.currency)
                  )}
                </span>
              </div>

              <div className="flex items-center justify-between text-xs text-[#B8B8C3]">
                <span>📥 {product.downloads.toLocaleString()} {t('marketplace.downloads')}</span>
                <span>⭐ {product.rating.toFixed(1)} ({product.reviewCount})</span>
              </div>

              <div className="flex items-center justify-between text-xs text-[#B8B8C3]">
                <span>{formatFileSize(product.fileSize)}</span>
                <span>{product.category}</span>
              </div>

              {product.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {product.tags.slice(0, 3).map((tag, idx) => (
                    <span key={idx} className="px-2 py-1 bg-[#15191E] text-xs text-cyan-400 rounded">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handleEdit(product)}
                className="flex-1 btn-secondary text-sm"
              >
                {t('common.edit')}
              </button>
              <button
                onClick={() => handleDelete(product.id)}
                className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredProducts.length === 0 && (
        <div className="card-dark p-12 text-center">
          <div className="text-6xl mb-4">🛒</div>
          <h3 className="text-xl font-bold text-white mb-2">{t('marketplace.noProducts')}</h3>
          <p className="text-[#B8B8C3] mb-4">{t('marketplace.noProductsDesc')}</p>
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            className="btn-primary"
          >
            {t('marketplace.createFirstProduct')}
          </button>
        </div>
      )}

      {/* Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-white mb-6">
              {editing ? t('marketplace.editProduct') : t('marketplace.newProduct')}
            </h3>

            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('marketplace.productName')}
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="input-dark w-full"
                    placeholder="Premium Motion Pack"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    Termék Típusa
                  </label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value as Product['type'] })}
                    className="input-dark w-full"
                  >
                    <option value="motion_pack">{t('marketplace.productType.motion_pack')}</option>
                    <option value="voice_pack">{t('marketplace.productType.voice_pack')}</option>
                    <option value="training_pack">{t('marketplace.productType.training_pack')}</option>
                    <option value="theme">{t('marketplace.productType.theme')}</option>
                    <option value="achievement_pack">{t('marketplace.productType.achievement_pack')}</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('marketplace.category')}
                  </label>
                  <input
                    type="text"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="input-dark w-full"
                    placeholder="Professional, Beginner, Advanced..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('marketplace.version')}
                  </label>
                  <input
                    type="text"
                    value={form.version}
                    onChange={(e) => setForm({ ...form, version: e.target.value })}
                    className="input-dark w-full"
                    placeholder="1.0.0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('marketplace.description')}
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="input-dark w-full"
                  rows={3}
                  placeholder={t('marketplace.descriptionPlaceholder')}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('marketplace.price')}
                  </label>
                  <input
                    type="number"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: Number(e.target.value) })}
                    className="input-dark w-full"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('marketplace.discountPrice')}
                  </label>
                  <input
                    type="number"
                    value={form.discountPrice}
                    onChange={(e) => setForm({ ...form, discountPrice: Number(e.target.value) })}
                    className="input-dark w-full"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('marketplace.currency')}
                  </label>
                  <select
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value as Product['currency'] })}
                    className="input-dark w-full"
                  >
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="HUF">HUF (Ft)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('marketplace.fileUrl')}
                  </label>
                  <input
                    type="text"
                    value={form.fileUrl}
                    onChange={(e) => setForm({ ...form, fileUrl: e.target.value })}
                    className="input-dark w-full"
                    placeholder="https://storage.dartiqon.com/products/..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('marketplace.thumbnailUrl')}
                  </label>
                  <input
                    type="text"
                    value={form.thumbnailUrl}
                    onChange={(e) => setForm({ ...form, thumbnailUrl: e.target.value })}
                    className="input-dark w-full"
                    placeholder="https://storage.dartiqon.com/thumbnails/..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('marketplace.fileSize')} (bytes)
                </label>
                <input
                  type="number"
                  value={form.fileSize}
                  onChange={(e) => setForm({ ...form, fileSize: Number(e.target.value) })}
                  className="input-dark w-full"
                  placeholder="1048576"
                />
              </div>

              <div className="border-t border-[#31383D] pt-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-lg font-semibold text-white">{t('marketplace.requirements')}</h4>
                  <button
                    type="button"
                    onClick={addRequirement}
                    className="text-sm text-cyan-400 hover:text-cyan-300"
                  >
                    + {t('marketplace.addRequirement')}
                  </button>
                </div>
                <div className="space-y-2">
                  {form.requirements.map((req, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input
                        type="text"
                        value={req}
                        onChange={(e) => updateRequirement(idx, e.target.value)}
                        className="input-dark flex-1"
                        placeholder="Dartiqon v2.0+"
                      />
                      <button
                        onClick={() => removeRequirement(idx)}
                        className="px-3 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-[#31383D] pt-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-lg font-semibold text-white">{t('marketplace.tags')}</h4>
                  <button
                    type="button"
                    onClick={addTag}
                    className="text-sm text-cyan-400 hover:text-cyan-300"
                  >
                    + {t('marketplace.addTag')}
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {form.tags.map((tag, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input
                        type="text"
                        value={tag}
                        onChange={(e) => updateTag(idx, e.target.value)}
                        className="input-dark flex-1"
                        placeholder="premium"
                      />
                      <button
                        onClick={() => removeTag(idx)}
                        className="px-3 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.featured}
                    onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <span className="text-sm text-[#B8B8C3]">{t('marketplace.featured')}</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.enabled}
                    onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <span className="text-sm text-[#B8B8C3]">{t('marketplace.available')}</span>
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={handleSave} className="btn-primary flex-1">
                {t('common.save')}
              </button>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditing(null);
                  resetForm();
                }}
                className="btn-secondary flex-1"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCatalog;
